'use client';

/**
 * Motion primitives for the flow system (design/ART_DIRECTION.md §4).
 * Transform/opacity only; every primitive respects prefers-reduced-motion.
 */
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { ReactNode, useRef } from 'react';

export const FLOW_EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

/** Subtle scroll-linked parallax. `speed` in px of travel across the
 *  viewport pass; positive drifts up. Clamp is deliberate — this is depth,
 *  not a ride. */
export function Parallax({
  children,
  speed = 24,
  className = '',
}: {
  children: ReactNode;
  speed?: number;
  className?: string;
}) {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const raw = useTransform(scrollYProgress, [0, 1], [speed, -speed]);
  const y = useSpring(raw, { stiffness: 60, damping: 20, mass: 0.6 });

  return (
    <div ref={ref} className={className}>
      <motion.div style={reduced ? undefined : { y }}>{children}</motion.div>
    </div>
  );
}

/** Spring hover/tap wrapper for cards — physics, not transitions. */
export function SpringCard({
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
}) {
  const reduced = useReducedMotion();
  if (reduced) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.99 }}
      transition={{ type: 'spring', stiffness: 320, damping: 24 }}
    >
      {children}
    </motion.div>
  );
}
